import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useTerminal } from '../lib/store';
import { TopBar } from '../components/TopBar';
import { ActionButton, Avatar, LiveTimer, Pill } from '../components/ui';
import { ReasonPicker } from '../components/pickers';

/**
 * Menú principal.
 *
 * Conserva la división del terminal clásico —presencia a la izquierda,
 * producción a la derecha— porque es la que el taller ya tiene aprendida, pero
 * la fase en marcha pasa a ser lo primero que se ve, con su cronómetro en vivo.
 */
export function Home() {
  const { state, catalog, clock, pausePhase, notify, busy } = useTerminal();
  const navigate = useNavigate();
  const [exitReason, setExitReason] = useState(false);
  const [pauseReason, setPauseReason] = useState(false);

  if (!state) return null;
  const { employee, presence, activeSession, can } = state;
  const params = catalog?.parameters ?? {};
  const requireExitReason = params['presence.requireExitReason'] === 'true';
  const phaseName = (code: string) => catalog?.phases.find((p) => p.code === code)?.name ?? code;

  const guard = async (fn: () => Promise<void>) => {
    try {
      await fn();
    } catch (err) {
      notify({ kind: 'error', title: 'No se pudo completar', body: err instanceof ApiError ? err.message : String(err) });
    }
  };

  return (
    <div className="app">
      <TopBar title="Terminal de producción" subtitle={`${employee.fullName}${employee.jobTitle ? ` · ${employee.jobTitle}` : ''}`} />

      <div className="content stack">
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <Avatar name={employee.fullName} />
          <div>
            <div className="h2">{employee.fullName}</div>
            <div className="muted small">{employee.jobTitle ?? 'Operario'}</div>
          </div>
          <div className="spacer" />
          {employee.supervisor ? <Pill tone="accent">Jefe de producción</Pill> : null}
          <Pill tone={presence.inside ? 'ok' : 'warn'}>{presence.inside ? 'Dentro' : 'Fuera'}</Pill>
        </div>

        {activeSession ? (
          <div className="banner banner--running">
            <div>
              <div className="eyebrow">Fase en marcha</div>
              <div className="h2">
                OF {activeSession.orderDocNum} · {phaseName(activeSession.phaseCode)}
              </div>
            </div>
            <div className="spacer" />
            <LiveTimer startedAt={activeSession.startedAt} className="timer timer--sm timer--running" />
            <button className="btn btn--warn btn--lg" disabled={busy} onClick={() => setPauseReason(true)}>
              Parar fase
            </button>
          </div>
        ) : null}

        <div className="grid-2">
          <section className="card stack">
            <div className="eyebrow">Presencia</div>
            <ActionButton
              label="Entrada"
              hint={presence.inside ? 'Ya has fichado la entrada' : 'Ficha al llegar al puesto'}
              tone="ok"
              disabled={!can.clockIn || busy}
              onClick={() => void guard(() => clock('in'))}
            />
            <ActionButton
              label="Salida"
              hint={
                params['presence.autoPausePhase'] === 'true' && activeSession
                  ? 'Pausará la fase en marcha y la reanudará al volver'
                  : 'Ficha al terminar o al salir'
              }
              tone="warn"
              disabled={!can.clockOut || busy}
              onClick={() => setExitReason(true)}
            />
            {presence.since ? (
              <p className="muted small" style={{ margin: 0 }}>
                {presence.inside ? 'Dentro desde' : 'Fuera desde'} las{' '}
                {new Date(presence.since).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </p>
            ) : null}
          </section>

          <section className="card stack">
            <div className="eyebrow">Producción</div>
            <ActionButton
              label="Inicio de fase"
              hint="Empieza a imputar tiempo en una OF"
              tone="accent"
              disabled={!can.startPhase || busy}
              onClick={() => navigate('/start')}
            />
            <ActionButton
              label="Finalizar fase"
              hint="Emite el tiempo y los consumos de la fase"
              disabled={!can.finishPhase || busy}
              onClick={() => navigate('/finish')}
            />
            <ActionButton
              label="Cerrar orden"
              hint="Recibe lo fabricado y cierra la OF"
              disabled={!can.closeOrder || busy}
              onClick={() => navigate('/close')}
            />
          </section>
        </div>

        <div className="row row--wrap">
          <button className="btn btn--ghost" onClick={() => navigate('/monitor')}>
            📊 Monitor de producción
          </button>
          {employee.supervisor && params['terminal.showParameters'] !== 'false' ? (
            <button className="btn btn--ghost" onClick={() => navigate('/admin')}>
              ⚙️ Configuración y envíos a SAP
            </button>
          ) : null}
        </div>
      </div>

      {exitReason ? (
        <ReasonPicker
          title="Causa de salida"
          subtitle={requireExitReason ? 'Obligatorio: elige el motivo' : 'Opcional: puedes salir sin indicar causa'}
          reasons={(catalog?.stopReasons ?? []).filter((r) => r.kind === 'presencia')}
          optional={!requireExitReason}
          onClose={() => setExitReason(false)}
          onPick={(code) => {
            setExitReason(false);
            void guard(() => clock('out', code));
          }}
        />
      ) : null}

      {pauseReason && activeSession ? (
        <ReasonPicker
          title="¿Por qué paras la fase?"
          subtitle="Si indicas una causa, la fase queda bloqueada hasta que se resuelva la incidencia."
          reasons={(catalog?.stopReasons ?? []).filter((r) => r.kind === 'fase')}
          optional
          onClose={() => setPauseReason(false)}
          onPick={(code) => {
            setPauseReason(false);
            void guard(() => pausePhase({ sessionId: activeSession.id, reasonCode: code }));
          }}
        />
      ) : null}
    </div>
  );
}
